import { Injectable, Inject, forwardRef } from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import { MapSymbolFactory } from './map-symbol-factory.service';
import { ImageProcessingService } from './image-processing.service';
import { ColoredMarkerService } from './colored-marker.service';
import { DrilldownModeService } from './drilldown-mode.service';
import { LabelByOption, MapLabelConfig, RenderMode } from '../types/map.types';

/**
 * Marker data item with coordinates and attributes
 */
export interface MarkerItem {
  id?: string;
  uuid?: string;
  latitude: number;
  longitude: number;
  data?: any;
  geometry?: { latitude: number; longitude: number };
  direction?: number;
  thumb_url?: string;
  original_url?: string;
  site_uuid?: string;
  siteId?: string;
  site_id?: string;
  source_type?: string;
  source_uuid?: string;
  bid_item?: { id?: string; site_id?: string; site?: { id?: string } };
  siteName?: string;
}

/**
 * Grouped items at a single location
 */
export interface LocationGroup {
  pictures: MarkerItem[];
  bidItems: MarkerItem[];
  activities: MarkerItem[];
  deliveries: MarkerItem[];
  forms: MarkerItem[];
}

/**
 * Symbol data for creating graphics
 */
export interface MarkerSymbolData {
  uuid: string;
  symbol: any;
  attributes: Record<string, any>;
  markerType: string;
}

/**
 * Layer configuration for unified markers
 */
export interface UnifiedLayerConfig {
  showPictures: boolean;
  selectedBidItemIds?: string[];
}

/**
 * Click handler context for routing marker clicks
 */
export interface MarkerClickContext {
  onPictureClick: (graphic: Graphic, event: any) => void;
  onSameLocationClick: (graphic: Graphic) => void;
  onBidItemClick: (graphic: Graphic, event: any) => void;
  onActivityClick: (graphic: Graphic, event: any) => void;
  onDeliveryClick: (graphic: Graphic, event: any) => void;
  onFormClick: (graphic: Graphic, event: any) => void;
  onMultiItemClick: (graphic: Graphic, event: any) => void;
  onClusterZoom: (graphic: Graphic) => void;
}

/**
 * Hover data for picture preview
 */
export interface PictureHoverData {
  pictures: MarkerItem[];
  screenPoint: { x: number; y: number };
  markerUuid: string;
}

/**
 * Hover handler context for picture preview
 */
export interface MarkerHoverContext {
  onPictureHover: (data: PictureHoverData | null) => void;
}

/**
 * Fields for the unified markers FeatureLayer
 */
const UNIFIED_MARKER_FIELDS: __esri.FieldProperties[] = [
  { name: 'ObjectID', type: 'oid' },
  { name: 'uuid', type: 'string' },
  { name: 'markerType', type: 'string' },
  { name: 'longitude', type: 'double' },
  { name: 'latitude', type: 'double' },
  { name: 'count', type: 'integer' },
  { name: 'is_same_location', type: 'integer' },
  { name: 'picture_uuids', type: 'string' },
  { name: 'site_id', type: 'string' },
  { name: 'created_by_id', type: 'string' },
  { name: 'is_hovered_site', type: 'integer' },
  { name: 'bid_item_id', type: 'string' },
  { name: 'report_bid_item_id', type: 'string' },
  { name: 'activity_id', type: 'string' },
  { name: 'delivery_id', type: 'string' },
  { name: 'form_id', type: 'string' },
  { name: 'thumb_url', type: 'string' },
  { name: 'original_url', type: 'string' },
  { name: 'direction', type: 'double' },
  { name: 'site_uuid', type: 'string' },
  { name: 'site_name', type: 'string' },
  { name: 'report_uuid', type: 'string' },
  { name: 'report_date', type: 'string' },
  { name: 'reportDate', type: 'string' },  // camelCase version for hover effect
  { name: 'reportId', type: 'string' },
  { name: 'createdById', type: 'string' },  // camelCase version for inspector hover
  { name: 'source_type', type: 'string' },
  { name: 'source_uuid', type: 'string' },
  { name: 'source_name', type: 'string' },
  { name: 'created_at', type: 'string' },
  { name: 'is_multi_item_location', type: 'integer' },
  { name: 'location_key', type: 'string' },
  { name: 'pictures_count', type: 'integer' },
  { name: 'bid_items_count', type: 'integer' },
  { name: 'activities_count', type: 'integer' },
  { name: 'deliveries_count', type: 'integer' },
  { name: 'forms_count', type: 'integer' },
  { name: 'bid_item_ids', type: 'string' },
  { name: 'bid_item_name', type: 'string' },
  { name: 'bid_item_code', type: 'string' },
  { name: 'bid_item_unit', type: 'string' },
  { name: 'quantity', type: 'double' },
  { name: 'title', type: 'string' }
];

/**
 * Fields for the as-built centroid label FeatureLayer
 */
const AS_BUILT_LABEL_FIELDS: any[] = [
  { name: 'ObjectID', type: 'oid' },
  { name: 'bid_item_name', type: 'string' },
  { name: 'bid_item_code', type: 'string' },
  { name: 'bid_item_unit', type: 'string' },
  { name: 'quantity', type: 'double' },
  { name: 'site_name', type: 'string' },
  { name: 'title', type: 'string' },
];

/**
 * UnifiedMarkerService
 *
 * Manages the unified markers layer that combines pictures, bid items,
 * activities, and deliveries into a single clustered FeatureLayer.
 *
 * Responsibilities:
 * - Building location mappings (site name -> ID, item ID -> site ID)
 * - Grouping items by location for multi-item scenarios
 * - Creating CIM symbols for different marker types
 * - Managing the FeatureLayer lifecycle with diff-based updates
 * - Handling clustering configuration
 * - Setting up click handlers
 *
 * Part of Phase 4.7 of the map maintainability refactor.
 */
@Injectable({ providedIn: 'root' })
export class UnifiedMarkerService {
  /** The managed unified markers layer */
  private layer: FeatureLayer | null = null;

  /** FeatureLayer for as-built centroid labels (invisible points, labels only) */
  private asBuiltLabelLayer: FeatureLayer | null = null;

  /** GraphicsLayer for deconflicted text labels (client-side collision detection) */
  private asBuiltTextLabelLayer: GraphicsLayer | null = null;

  /** Candidate labels managed by the deconfliction algorithm */
  private labelCandidates: Array<{
    graphic: Graphic;
    longitude: number;
    latitude: number;
    text: string;
    priority: number;
    widthPx: number;
    heightPx: number;
  }> = [];

  /** Handle for the viewpoint watcher that drives label recomputation */
  private labelViewpointHandle: any = null;
  private labelConfigCache: MapLabelConfig | null = null;

  /** Cache of previous marker UUIDs for diff-based updates */
  private previousMarkerUuids = new Set<string>();

  /** Cache of previous markers hash for change detection */
  private previousMarkersHash = '';

  /** Whether previous symbols included CIM (for layer recreation detection) */
  private previousSymbolsHadCIM = false;

  /** Previous view mode (for layer recreation when color scheme changes) */
  private previousViewMode: string | null = null;

  /** Map of marker UUID -> pictures array for click handling */
  private markerPicturesMap = new Map<string, MarkerItem[]>();

  /** Map of marker UUID -> multi-item location data */
  private multiItemLocationsMap = new Map<string, LocationGroup>();

  /** Click handler reference for cleanup */
  private clickHandler: __esri.Handle | null = null;

  /** Pointer move handler for cursor changes */
  private pointerMoveHandler: __esri.Handle | null = null;

  /** Clustering zoom watch handler */
  private clusteringZoomWatchHandle: __esri.WatchHandle | null = null;

  /** Cached symbol data used to rebuild the pin renderer when switching render modes */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/array-type
  private lastSymbolData: Array<{ uuid: string; symbol: any }> = [];

  /** MapView reference captured when the layer is created, used by setRenderMode */
  private currentMapView: __esri.MapView | null = null;

  /**
   * The user's most recently requested render mode. The default 'pins' value
   * matches the initial auto-clustering-by-zoom behavior set up in
   * createNewLayer. When the user explicitly calls setRenderMode, the zoom
   * watcher checks this before flipping featureReduction so an explicit
   * choice isn't silently overridden on zoom change.
   */
  private currentRenderMode: RenderMode = 'pins';

  /** Hover context for picture preview */
  private hoverContext: MarkerHoverContext | null = null;

  /** Currently hovered marker UUID (for deduplication) */
  private currentlyHoveredMarkerUuid: string | null = null;

  /** Hover debounce timeout */
  private hoverDebounceTimeout: any = null;

  /** Hover delay in ms (UX best practice: slight delay prevents flicker) */
  private readonly HOVER_DELAY = 150;

  /** Hover leave delay in ms (prevents flicker when moving between close markers) */
  private readonly HOVER_LEAVE_DELAY = 100;

  constructor(
    private mapSymbolFactory: MapSymbolFactory,
    private imageProcessingService: ImageProcessingService,
    private coloredMarkerService: ColoredMarkerService,
    @Inject(forwardRef(() => DrilldownModeService)) private drilldownModeService: DrilldownModeService
  ) {}

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Get the current unified markers layer
   */
  getLayer(): FeatureLayer | null {
    return this.layer;
  }

  /**
   * Get pictures for a marker by UUID
   */
  getPicturesForMarker(uuid: string): MarkerItem[] | undefined {
    return this.markerPicturesMap.get(uuid);
  }

  /**
   * Get multi-item location data by UUID
   */
  getMultiItemData(uuid: string): LocationGroup | undefined {
    return this.multiItemLocationsMap.get(uuid);
  }

  /**
   * Set hover context for picture preview
   */
  setHoverContext(context: MarkerHoverContext | null): void {
    this.hoverContext = context;
    if (!context) {
      this.clearHoverState();
    }
  }

  /**
   * Clear hover state (call when component destroys or map changes)
   */
  clearHoverState(): void {
    if (this.hoverDebounceTimeout) {
      clearTimeout(this.hoverDebounceTimeout);
      this.hoverDebounceTimeout = null;
    }
    this.currentlyHoveredMarkerUuid = null;
    if (this.hoverContext) {
      this.hoverContext.onPictureHover(null);
    }
  }

  /**
   * Build mappings from sites and markers for site ID lookups
   */
  buildLocationMappings(
    sites: any[],
    bidItemMarkers: MarkerItem[],
    activityMarkers: MarkerItem[],
    deliveryMarkers: MarkerItem[]
  ): {
    siteNameToId: Map<string, string>;
    itemIdToSiteId: Map<string, string>;
    reportBidItemToBidItemId: Map<string, string>;
    deliveryIdToBidItemId: Map<string, string>;
  } {
    const siteNameToId = new Map<string, string>();
    const itemIdToSiteId = new Map<string, string>();
    const reportBidItemToBidItemId = new Map<string, string>();
    const deliveryIdToBidItemId = new Map<string, string>();

    // Build siteName -> siteId map
    sites.forEach(site => {
      const siteId = site.site_id || site.id;
      const siteName = site.site_name || site.name;
      if (siteId && siteName) {
        siteNameToId.set(siteName, siteId);
      }
    });

    // Build reportBidItemId -> bidItemId map and itemId -> siteId map from bid items
    bidItemMarkers?.forEach(marker => {
      const siteName = (marker.data as any)?.siteName;
      const reportBidItemId = marker.data?.id;
      const bidItemId = marker.data?.bid_item?.id;

      if (reportBidItemId && bidItemId) {
        reportBidItemToBidItemId.set(reportBidItemId, bidItemId);
      }

      if (siteName) {
        const siteId = siteNameToId.get(siteName);
        if (siteId) {
          if (reportBidItemId) itemIdToSiteId.set(reportBidItemId, siteId);
          if (bidItemId) itemIdToSiteId.set(bidItemId, siteId);
        }
      }
    });

    // Build itemId -> siteId map from activities
    activityMarkers?.forEach(marker => {
      const siteName = (marker.data as any)?.siteName;
      const activityId = marker.data?.id;

      if (siteName && activityId) {
        const siteId = siteNameToId.get(siteName);
        if (siteId) itemIdToSiteId.set(activityId, siteId);
      }
    });

    // Build itemId -> siteId map and deliveryId -> bidItemId map from deliveries
    deliveryMarkers?.forEach(marker => {
      const siteName = (marker.data as any)?.siteName;
      const deliveryId = marker.data?.id;
      const bidItemId = marker.data?.bid_item?.id || marker.data?.bid_item_id;

      if (siteName && deliveryId) {
        const siteId = siteNameToId.get(siteName);
        if (siteId) itemIdToSiteId.set(deliveryId, siteId);
      }

      // Map delivery to bid item if linked
      if (deliveryId && bidItemId) {
        deliveryIdToBidItemId.set(deliveryId, bidItemId);
      }
    });

    return { siteNameToId, itemIdToSiteId, reportBidItemToBidItemId, deliveryIdToBidItemId };
  }

  /**
   * Group all items by location to detect multi-item scenarios
   * Uses 5 decimal places for grouping (~1 meter precision)
   *
   * When bid item filter is active:
   * - Activities are excluded (they have no bid item link)
   * - Deliveries are filtered to only those linked to selected bid items
   */
  groupItemsByLocation(
    pictureMarkers: MarkerItem[],
    bidItemMarkers: MarkerItem[],
    activityMarkers: MarkerItem[],
    deliveryMarkers: MarkerItem[],
    formMarkers: MarkerItem[],
    selectedBidItemIds?: string[],
    deliveryIdToBidItemId?: Map<string, string>
  ): Map<string, LocationGroup> {
    const locationMap = new Map<string, LocationGroup>();
    const hasActiveFilter = selectedBidItemIds && selectedBidItemIds.length > 0;

    // Helper to create location key using grid-based grouping
    // Uses ~3.7 meter grid cells (27000 multiplier) to handle GPS drift
    // Round instead of floor centers grid cells and reduces boundary splitting
    const getLocationKey = (lat: number, lng: number): string => {
      // 27000 multiplier = ~3.7m grid cells at equator, ~3m at 34° latitude
      // Using Math.round centers the grid cells, reducing edge cases where
      // items 0.5m apart end up in different cells
      const gridLat = Math.round(lat * 27000);
      const gridLng = Math.round(lng * 27000);
      return `${gridLat},${gridLng}`;
    };

    // Helper to add item to location
    const addToLocation = (
      lat: number,
      lng: number,
      item: MarkerItem,
      type: 'pictures' | 'bidItems' | 'activities' | 'deliveries' | 'forms'
    ) => {
      const key = getLocationKey(lat, lng);
      if (!locationMap.has(key)) {
        locationMap.set(key, { pictures: [], bidItems: [], activities: [], deliveries: [], forms: [] });
      }
      locationMap.get(key)![type].push(item);
    };

    // Add pictures
    pictureMarkers?.forEach(pic => {
      if (pic.latitude != null && pic.longitude != null) {
        addToLocation(parseFloat(pic.latitude as any), parseFloat(pic.longitude as any), pic, 'pictures');
      }
    });

    // Add bid items (respect filter if active)
    bidItemMarkers?.forEach(marker => {
      if (hasActiveFilter && !selectedBidItemIds!.includes(marker.data?.bid_item?.id)) {
        return;
      }
      const lat = marker.geometry?.latitude ?? marker.data?.geometry?.latitude;
      const lng = marker.geometry?.longitude ?? marker.data?.geometry?.longitude;
      if (lat != null && lng != null) {
        addToLocation(parseFloat(lat), parseFloat(lng), marker, 'bidItems');
      }
    });

    // Add activities (skip all when bid item filter is active - activities have no bid item link)
    if (!hasActiveFilter) {
      activityMarkers?.forEach(marker => {
        const lat = marker.geometry?.latitude ?? marker.data?.geometry?.latitude;
        const lng = marker.geometry?.longitude ?? marker.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          addToLocation(parseFloat(lat), parseFloat(lng), marker, 'activities');
        }
      });
    }

    // Add deliveries (filter by bid item link when filter is active)
    deliveryMarkers?.forEach(marker => {
      if (hasActiveFilter) {
        // Only show deliveries linked to selected bid items
        const deliveryId = marker.data?.id;
        const deliveryBidItemId = deliveryIdToBidItemId?.get(deliveryId) ||
          marker.data?.bid_item?.id ||
          marker.data?.bid_item_id;
        if (!deliveryBidItemId || !selectedBidItemIds!.includes(deliveryBidItemId)) {
          return;
        }
      }
      const lat = marker.geometry?.latitude ?? marker.data?.geometry?.latitude;
      const lng = marker.geometry?.longitude ?? marker.data?.geometry?.longitude;
      if (lat != null && lng != null) {
        addToLocation(parseFloat(lat), parseFloat(lng), marker, 'deliveries');
      }
    });

    // Add forms (skip all when bid item filter is active - forms have no bid item link, mirroring activities)
    if (!hasActiveFilter) {
      formMarkers?.forEach(marker => {
        const lat = marker.geometry?.latitude ?? marker.data?.geometry?.latitude;
        const lng = marker.geometry?.longitude ?? marker.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          addToLocation(parseFloat(lat), parseFloat(lng), marker, 'forms');
        }
      });
    }

    return locationMap;
  }

  /**
   * Create marker symbols for all items
   * Returns array of symbol data ready to be converted to graphics
   *
   * When bid item filter is active:
   * - Activities are hidden (no bid item link)
   * - Deliveries only show if linked to selected bid items
   * - Pictures only show if linked to selected bid items via:
   *   - source_type='report_bid_item' where bid item matches
   *   - source_type='report_material_delivery' where delivery is linked to matching bid item
   */
  async createMarkerSymbols(
    pictureMarkers: MarkerItem[],
    bidItemMarkers: MarkerItem[],
    activityMarkers: MarkerItem[],
    deliveryMarkers: MarkerItem[],
    formMarkers: MarkerItem[],
    itemIdToSiteId: Map<string, string>,
    reportBidItemToBidItemId: Map<string, string>,
    selectedBidItemIds?: string[],
    deliveryIdToBidItemId?: Map<string, string>,
    colorConfig?: {
      viewMode: 'site' | 'bid_item' | 'day' | 'inspector';
      colorMap: { [key: string]: string };
    }
  ): Promise<MarkerSymbolData[]> {
    const allSymbols: MarkerSymbolData[] = [];
    const hasActiveFilter = selectedBidItemIds && selectedBidItemIds.length > 0;

    // Build colored marker URL map from palette (sync - using pre-generated PNGs)
    const coloredMarkerUrlMap = new Map<string, { bid_item: string; activity: string; delivery: string }>();
    if (colorConfig?.colorMap && Object.keys(colorConfig.colorMap).length > 0) {
      const uniqueColors = Array.from(new Set(Object.values(colorConfig.colorMap)));
      uniqueColors.forEach((color) => {
        coloredMarkerUrlMap.set(color, {
          bid_item: this.coloredMarkerService.getColoredMarkerUrl('bid_item', color),
          activity: this.coloredMarkerService.getColoredMarkerUrl('activity', color),
          delivery: this.coloredMarkerService.getColoredMarkerUrl('delivery', color)
        });
      });
    }

    // Clear maps for this run
    this.markerPicturesMap.clear();
    this.multiItemLocationsMap.clear();

    // Filter pictures by bid item association when filter is active
    let filteredPictureMarkers = pictureMarkers;
    if (hasActiveFilter && pictureMarkers?.length > 0) {
      filteredPictureMarkers = pictureMarkers.filter(pic => {
        // Check if picture is linked to a selected bid item via report_bid_item
        if (pic.source_type === 'report_bid_item' && pic.source_uuid) {
          const bidItemId = reportBidItemToBidItemId.get(pic.source_uuid);
          return bidItemId && selectedBidItemIds!.includes(bidItemId);
        }
        // Check if picture is linked to a delivery that's linked to a selected bid item
        if (pic.source_type === 'report_material_delivery' && pic.source_uuid && deliveryIdToBidItemId) {
          const deliveryBidItemId = deliveryIdToBidItemId.get(pic.source_uuid);
          return deliveryBidItemId && selectedBidItemIds!.includes(deliveryBidItemId);
        }
        // Picture not linked to bid item - hide when filter is active
        return false;
      });
    }

    // Group all items by location (activities and deliveries already filtered inside)
    const itemsByLocation = this.groupItemsByLocation(
      filteredPictureMarkers,
      bidItemMarkers,
      activityMarkers,
      deliveryMarkers,
      formMarkers,
      selectedBidItemIds,
      deliveryIdToBidItemId
    );

    // Track which items are in multi-item locations
    const picturesInMultiItem = new Set<string>();
    const bidItemsInMultiItem = new Set<string>();
    const activitiesInMultiItem = new Set<string>();
    const deliveriesInMultiItem = new Set<string>();
    const formsInMultiItem = new Set<string>();

    // Categorize locations
    const sameLocationPictureGroups: Array<{ location: string; pictures: MarkerItem[] }> = [];
    const multiItemLocationGroups: Array<{ location: string; data: LocationGroup }> = [];
    const individualPictures: MarkerItem[] = [];
    const processedPictureUuids = new Set<string>();

    itemsByLocation.forEach((items, locationKey) => {
      const totalCount = items.pictures.length + items.bidItems.length +
                         items.activities.length + items.deliveries.length + items.forms.length;
      const hasItems = items.bidItems.length > 0 || items.activities.length > 0 ||
                       items.deliveries.length > 0 || items.forms.length > 0;
      const hasPictures = items.pictures.length > 0;

      // Check if this is a single item with pictures at the same location
      // In this case, show the item marker instead of a multi-item marker
      // (pictures are accessible through the item's popup or clicking shows all pictures at location)
      const isSingleItemWithPictures = (): boolean => {
        if (!hasPictures || !hasItems) return false;

        // Must have exactly 1 item total (bid item, activity, delivery, or form)
        const itemCount = items.bidItems.length + items.activities.length +
                          items.deliveries.length + items.forms.length;
        return itemCount === 1;
      };

      if (totalCount > 1) {
        if (hasPictures && !hasItems && items.pictures.length >= 2) {
          // Pictures only (2+) -> Thumbnail + badge
          sameLocationPictureGroups.push({ location: locationKey, pictures: items.pictures });
          items.pictures.forEach(p => {
            picturesInMultiItem.add(p.uuid!);
            processedPictureUuids.add(p.uuid!);
          });
        } else if (isSingleItemWithPictures()) {
          // Single item + pictures at same location -> Show as regular item marker (not multi-item)
          // Mark pictures as processed so they don't get individual markers
          items.pictures.forEach(p => processedPictureUuids.add(p.uuid!));
          // Don't add items to multiItem sets - they'll be rendered as normal markers
        } else if (hasItems) {
          // Multiple items (2+) at same location -> Cluster marker + popover
          multiItemLocationGroups.push({ location: locationKey, data: items });
          items.pictures.forEach(p => {
            picturesInMultiItem.add(p.uuid!);
            processedPictureUuids.add(p.uuid!);
          });
          items.bidItems.forEach(b => bidItemsInMultiItem.add(b.data?.id));
          items.activities.forEach(a => activitiesInMultiItem.add(a.data?.id));
          items.deliveries.forEach(d => deliveriesInMultiItem.add(d.data?.id));
          items.forms.forEach(f => formsInMultiItem.add(f.data?.id));
        }
      } else if (totalCount === 1 && hasPictures) {
        // Single picture
        individualPictures.push(...items.pictures);
        items.pictures.forEach(p => processedPictureUuids.add(p.uuid!));
      }
    });

    // Process multi-item location markers
    allSymbols.push(...this.createMultiItemSymbols(
      multiItemLocationGroups,
      itemIdToSiteId
    ));

    // Process picture markers (use filteredPictureMarkers when filter is active)
    if (filteredPictureMarkers?.length > 0) {
      // Add any unprocessed pictures from the filtered list
      filteredPictureMarkers.forEach(pic => {
        if (pic.latitude != null && pic.longitude != null && !processedPictureUuids.has(pic.uuid!)) {
          individualPictures.push(pic);
        }
      });

      // Process same-location picture groups
      const sameLocationSymbols = await this.createSameLocationPictureSymbols(
        sameLocationPictureGroups,
        itemIdToSiteId,
        reportBidItemToBidItemId
      );
      allSymbols.push(...sameLocationSymbols);

      // Process individual pictures
      const pictureSymbols = await this.createIndividualPictureSymbols(
        individualPictures,
        itemIdToSiteId,
        reportBidItemToBidItemId
      );
      allSymbols.push(...pictureSymbols);
    }

    // Add bid item symbols
    const filteredBidItems = bidItemMarkers?.filter(marker =>
      !bidItemsInMultiItem.has(marker.data?.id) &&
      (!hasActiveFilter || selectedBidItemIds!.includes(marker.data?.bid_item?.id))
    ) || [];

    filteredBidItems.forEach(marker => {
      allSymbols.push(this.createBidItemSymbol(marker, itemIdToSiteId, colorConfig, coloredMarkerUrlMap));
    });

    // Add activity symbols (skip all when bid item filter is active - activities have no bid item link)
    if (!hasActiveFilter) {
      const filteredActivities = activityMarkers?.filter(marker =>
        !activitiesInMultiItem.has(marker.data?.id)
      ) || [];

      filteredActivities.forEach(marker => {
        allSymbols.push(this.createActivitySymbol(marker, itemIdToSiteId, colorConfig, coloredMarkerUrlMap));
      });
    }

    // Add delivery symbols (filter by bid item link when filter is active)
    const filteredDeliveries = deliveryMarkers?.filter(marker => {
      if (deliveriesInMultiItem.has(marker.data?.id)) {
        return false;
      }
      if (hasActiveFilter) {
        // Only show deliveries linked to selected bid items
        const deliveryId = marker.data?.id;
        const deliveryBidItemId = deliveryIdToBidItemId?.get(deliveryId) ||
          marker.data?.bid_item?.id ||
          marker.data?.bid_item_id;
        return deliveryBidItemId && selectedBidItemIds!.includes(deliveryBidItemId);
      }
      return true;
    }) || [];

    filteredDeliveries.forEach(marker => {
      allSymbols.push(this.createDeliverySymbol(marker, itemIdToSiteId, colorConfig, coloredMarkerUrlMap));
    });

    // Add form symbols (skip all when bid item filter is active - forms have no bid item link, mirroring activities)
    if (!hasActiveFilter) {
      const filteredForms = formMarkers?.filter(marker =>
        !formsInMultiItem.has(marker.data?.id)
      ) || [];

      filteredForms.forEach(marker => {
        allSymbols.push(this.createFormSymbol(marker, itemIdToSiteId));
      });
    }

    return allSymbols;
  }

  /**
   * Create graphics from symbol data
   */
  createGraphics(symbolData: MarkerSymbolData[]): Graphic[] {
    return symbolData.map((data, index) => {
      const attrs = data.attributes;
      const wgs84Point = new Point({
        longitude: attrs.longitude,
        latitude: attrs.latitude,
        spatialReference: { wkid: 4326 }
      });

      const webMercatorPoint = webMercatorUtils.geographicToWebMercator(wgs84Point) as Point;

      return new Graphic({
        geometry: webMercatorPoint,
        attributes: {
          ObjectID: index + 1,
          ...attrs,
          uuid: data.uuid,
          markerType: data.markerType
        }
      });
    });
  }

  /**
   * Update or create the unified markers layer
   * Uses diff-based updates to minimize flickering
   */
  async updateLayer(
    mapView: __esri.MapView,
    symbolData: MarkerSymbolData[],
    config: UnifiedLayerConfig,
    disableClustering: boolean = false
  ): Promise<void> {
    const graphics = this.createGraphics(symbolData);

    // IMPORTANT: Check if existing layer is still in the map BEFORE hash check
    // This prevents a bug where the layer was removed but the reference still exists,
    // and the hash matches, causing an early return without creating a new layer
    if (this.layer && !mapView.map.findLayerById(this.layer.id)) {
      this.layer = null;
      this.previousMarkerUuids.clear();
      this.previousMarkersHash = ''; // Reset hash to force recreation
    }

    // Always refresh the symbol-data cache before the hash early-return so a
    // subsequent setRenderMode('pins' | 'clustered') can rebuild the
    // unique-value renderer from the latest symbols even when no marker churn
    // triggered a full rebuild.
    this.lastSymbolData = symbolData.map(s => ({ uuid: s.uuid, symbol: s.symbol }));

    // Check if data has changed (include symbol URL to detect color changes)
    const currentHash = symbolData
      .map(s => {
        const symbolUrl = s.symbol?.type === 'picture-marker' ? (s.symbol as any).url : '';
        return `${s.markerType}-${s.uuid}-${s.attributes.longitude}-${s.attributes.latitude}-${symbolUrl}`;
      })
      .sort()
      .join('|');

    if (currentHash === this.previousMarkersHash && this.layer) {
      // No marker churn — but the active renderer can still be stale relative to
      // the latest symbolData (e.g. when a marker type such as forms arrives in a
      // later emission and an earlier renderer was built without it). Re-apply the
      // pin renderer from the freshest symbol data so every marker's per-uuid
      // symbol is guaranteed to be in uniqueValueInfos. Skip under non-pins render
      // modes, which own the renderer themselves.
      if (this.currentRenderMode === 'pins' && this.layer.renderer?.type === 'unique-value') {
        const rendererUuids = new Set(
          ((this.layer.renderer as any).uniqueValueInfos || []).map((i: any) => i.value)
        );
        const missingSymbol = this.lastSymbolData.some(s => !rendererUuids.has(s.uuid));
        if (missingSymbol) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.layer.renderer = this.buildPinRenderer(this.lastSymbolData) as any;
          this.layer.refresh();
        }
      }
      return; // No marker churn
    }
    this.previousMarkersHash = currentHash;

    // Check if we need to recreate layer (CIM symbols being added)
    const hasCIM = symbolData.some(s => s.symbol?.type === 'cim');
    const needsRecreate = this.layer && hasCIM && !this.previousSymbolsHadCIM;
    this.previousSymbolsHadCIM = hasCIM;

    if (needsRecreate && this.layer) {
      mapView.map.remove(this.layer);
      this.layer = null;
      this.previousMarkerUuids.clear();
    }

    if (this.layer) {
      await this.updateExistingLayer(graphics, symbolData, config, disableClustering);
      this.layer.visible = true;
    } else {
      await this.createNewLayer(mapView, graphics, symbolData, config, disableClustering);
    }
  }

  /**
   * Set up early hover handler on the mapView (independent of layer existence)
   * This should be called once when the mapView is ready, not after each layer update
   */
  setupEarlyHoverHandler(mapView: __esri.MapView): void {
    // Only set up once - if already exists, skip
    if (this.pointerMoveHandler) return;

    // Helper to check if a layer is clickable (unified layer or temp drilldown picture layer)
    const isClickableLayer = (layer: any): boolean => {
      return layer === this.layer || layer?.id === 'temp-filtered-picture-layer';
    };

    // Pointer move handler for cursor and hover preview - works even if layer doesn't exist yet
    this.pointerMoveHandler = mapView.on('pointer-move', (event) => {
      // If no layer yet, just keep default cursor
      if (!this.layer) {
        return;
      }

      mapView.hitTest(event).then(response => {
        const hit = response.results.find(r =>
          (r as any).graphic && isClickableLayer((r as any).graphic.layer)
        );
        mapView.container.style.cursor = hit ? 'pointer' : 'default';

        // Handle picture hover preview
        this.handlePictureHover(hit ? (hit as any).graphic : null, event);
      });
    });
  }

  /**
   * Set up click handlers for the unified layer
   */
  setupClickHandlers(
    mapView: __esri.MapView,
    context: MarkerClickContext
  ): void {
    // Remove existing click handler only (keep pointer-move handler)
    if (this.clickHandler) {
      this.clickHandler.remove();
      this.clickHandler = null;
    }

    if (!this.layer) return;

    // Helper to check if a layer is clickable (unified layer or temp drilldown picture layer)
    const isClickableLayer = (layer: any): boolean => {
      return layer === this.layer || layer?.id === 'temp-filtered-picture-layer';
    };

    // Click handler
    this.clickHandler = mapView.on('click', (event) => {
      mapView.hitTest(event).then(response => {
        // A drilldown photo wins: the unified layer's own copy of it sits on top, hidden only by a featureEffect
        // (still hit-tested) and without its markerType, so taking it first dropped the click (no full-screen photo)
        const hit = response.results.find(r => (r as any).graphic?.layer?.id === 'temp-filtered-picture-layer')
          ?? response.results.find(r => (r as any).graphic && isClickableLayer((r as any).graphic.layer));

        if (!hit) return;

        const graphic = (hit as any).graphic;
        const clusterCount = graphic.attributes?.cluster_count;
        const isRealCluster = clusterCount !== undefined && clusterCount > 1;

        // Check if this is from the drilldown temp picture layer
        const isDrilldownPicture = (hit as any).graphic.layer?.id === 'temp-filtered-picture-layer';

        if (isRealCluster && !isDrilldownPicture) {
          // Handle cluster click (not for drilldown pictures)
          const clusterTypeUuid = graphic.attributes.cluster_type_uuid;
          if (clusterTypeUuid?.startsWith('multi-item-')) {
            this.handleClusterMultiItemClick(clusterTypeUuid, event, context);
          } else {
            context.onClusterZoom(graphic);
          }
          return;
        }

        // Handle individual marker click (including drilldown pictures)
        this.handleMarkerClick(graphic, event, context, isDrilldownPicture);
      });
    });

    // Note: pointer-move handler is now set up early via setupEarlyHoverHandler()
    // and is independent of the layer lifecycle
  }

  /**
   * Handle picture marker hover for preview tooltip
   * Implements UX best practices:
   * - Debounced show (prevents flicker on fast mouse movements)
   * - Debounced hide (allows moving between close markers)
   * - Only triggers for picture markers
   */
  private handlePictureHover(graphic: Graphic | null, event: any): void {
    // No hover context set - skip
    if (!this.hoverContext) return;

    // Clear any pending debounce
    if (this.hoverDebounceTimeout) {
      clearTimeout(this.hoverDebounceTimeout);
      this.hoverDebounceTimeout = null;
    }

    // No hit or not a picture marker - hide preview with delay
    if (!graphic) {
      this.hoverDebounceTimeout = setTimeout(() => {
        if (this.currentlyHoveredMarkerUuid) {
          this.currentlyHoveredMarkerUuid = null;
          this.hoverContext?.onPictureHover(null);
        }
      }, this.HOVER_LEAVE_DELAY);
      return;
    }

    const markerType = graphic.attributes?.markerType;
    const markerUuid = graphic.attributes?.uuid;
    const isSameLocation = graphic.attributes?.is_same_location === 1;
    const isCluster = graphic.attributes?.cluster_count > 1;

    // When clustering is enabled, markerType may be in cluster_type_markerType attribute
    // Also detect picture markers by UUID pattern or by checking markerPicturesMap
    // Note: Both same-location groups and individual pictures are stored in markerPicturesMap
    const isSameLocationByUuid = markerUuid?.startsWith('same-location-');
    const isInPicturesMap = this.markerPicturesMap.has(markerUuid);
    const hasPictureAttributes = !!graphic.attributes?.thumb_url || !!graphic.attributes?.original_url;
    const isPictureMarker = isSameLocationByUuid || isInPicturesMap || hasPictureAttributes;
    const effectiveMarkerType = markerType || graphic.attributes?.cluster_type_markerType || (isPictureMarker ? 'picture' : undefined);


    // Only show preview for picture markers (individual or same-location groups)
    // Skip clusters as they represent multiple different locations
    if (effectiveMarkerType !== 'picture' || isCluster) {
      // Not a picture - hide with delay
      this.hoverDebounceTimeout = setTimeout(() => {
        if (this.currentlyHoveredMarkerUuid) {
          this.currentlyHoveredMarkerUuid = null;
          this.hoverContext?.onPictureHover(null);
        }
      }, this.HOVER_LEAVE_DELAY);
      return;
    }

    // Same marker - just update position (mouse may have moved)
    if (markerUuid === this.currentlyHoveredMarkerUuid) {
      // Update position for the same marker
      const storedPictures = this.markerPicturesMap.get(markerUuid);
      if (storedPictures && storedPictures.length > 0) {
        this.hoverContext?.onPictureHover({
          pictures: storedPictures,
          screenPoint: { x: event.x, y: event.y },
          markerUuid
        });
      }
      return;
    }

    // New picture marker - show preview immediately (no delay when switching between markers)
    // Only use delay when first showing preview from no preview state
    const showDelay = this.currentlyHoveredMarkerUuid ? 0 : this.HOVER_DELAY;

    this.hoverDebounceTimeout = setTimeout(() => {
      // Get pictures for this marker
      let pictures: MarkerItem[] = [];

      // First try to get from markerPicturesMap (works for both same-location groups and individual pictures)
      const storedPictures = this.markerPicturesMap.get(markerUuid);
      if (storedPictures && storedPictures.length > 0) {
        pictures = storedPictures;
      } else {
        // Fallback 1: Check drilldown service's marker pictures map (for drilldown mode pictures)
        const drilldownPictures = this.drilldownModeService.getPicturesForMarker(markerUuid);
        if (drilldownPictures && drilldownPictures.length > 0) {
          pictures = drilldownPictures;
        } else {
          // Fallback 2: try to read from graphic attributes (when clustering is disabled)
          const pic: MarkerItem = {
            uuid: markerUuid,
            thumb_url: graphic.attributes?.thumb_url,
            original_url: graphic.attributes?.original_url,
            latitude: graphic.attributes?.latitude,
            longitude: graphic.attributes?.longitude,
            direction: graphic.attributes?.direction,
            siteName: graphic.attributes?.site_name,
            data: {
              created_at: graphic.attributes?.created_at,
            }
          };
          // Only add if we have a valid image URL
          if (pic.thumb_url || pic.original_url) {
            pictures = [pic];
          }
        }
      }

      if (pictures.length > 0) {
        this.currentlyHoveredMarkerUuid = markerUuid;
        this.hoverContext?.onPictureHover({
          pictures,
          screenPoint: { x: event.x, y: event.y },
          markerUuid
        });
      }
    }, showDelay);
  }

  /**
   * Get the clustering configuration
   * Styling matches Figma design: primary blue (#1036D6) fill, lighter blue (#6583FF) outline
   * Uses clusterMinSize/clusterMaxSize for dynamic sizing based on cluster_count
   */
  getClusteringConfig(): any {
    return {
      type: 'cluster',
      clusterRadius: '120px', // Increased from 80px to reduce overlap with badge markers
      clusterMinSize: '28px',
      clusterMaxSize: '56px',
      clusterMaxScale: 5000, // Stop clustering when scale is below 5000 (zoom 15+) to break up clusters earlier
      clusterTransitionDuration: 200,
      clusterTransitionDistance: 10,
      filter: {
        where: "markerType <> 'multi_item_location'"
      } as any,
      symbol: {
        type: 'simple-marker',
        style: 'circle',
        color: [16, 54, 214, 255], // #1036D6 - Primary blue
        outline: {
          color: [101, 131, 255, 255], // #6583FF - Blue 2 (lighter blue ring)
          width: 3
        }
      } as any,
      clusterProperties: {
        total_count: {
          onStatisticField: 'count',
          statisticType: 'sum'
        },
        picture_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'picture', $feature.count, 0)",
          statisticType: 'sum'
        },
        bid_item_count: {
          onStatisticField: 'count',
          onStatisticExpression: "IIF($feature.markerType == 'bid_item', $feature.count, 0)",
          statisticType: 'sum'
        }
      } as any,
      labelingInfo: [{
        deconflictionStrategy: 'none',
        labelExpressionInfo: {
          // Show "99+" for counts over 99, otherwise show the count
          expression: `
            var count = $feature.cluster_count;
            IIF(count > 99, "99+", Text(count, "#"))
          `
        },
        symbol: {
          type: 'text',
          color: [255, 255, 255, 255],
          font: {
            size: 14,
            family: 'Montserrat',
            weight: 'bold'
          },
          xoffset: 0,
          yoffset: 0
        },
        labelPlacement: 'center-center',
        minScale: 0,
        maxScale: 0
      }],
      popupTemplate: {
        title: 'Cluster Details',
        content: [{
          type: 'text',
          text: '<b>{total_count}</b> total items<br>{picture_count} pictures<br>{bid_item_count} bid items'
        }]
      } as any
    };
  }

  /**
   * Build the unique-value pin renderer from the provided symbol data.
   * Extracted so it can be re-applied when swapping render modes.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/array-type
  private buildPinRenderer(symbolData: Array<{ uuid: string; symbol: any }>): any {
    return {
      type: 'unique-value',
      field: 'uuid',
      uniqueValueInfos: symbolData.map(s => ({ value: s.uuid, symbol: s.symbol })),
      defaultSymbol: this.mapSymbolFactory.createPlaceholderMarkerSymbol(),
      visualVariables: [{
        type: 'opacity',
        field: 'is_hovered_site',
        stops: [
          { value: 0, opacity: 0.3 },
          { value: 1, opacity: 1.0 }
        ]
      }]
    };
  }

  /**
   * Switch the unified markers layer between pins/clustered/heatmap modes.
   * - pins: unique-value renderer, featureReduction disabled, auto-cluster watcher re-enabled.
   * - clustered: unique-value renderer, featureReduction forced on, zoom watcher torn down.
   * - heatmap: heatmap renderer weighted by `count`, featureReduction cleared.
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  public setRenderMode(mode: RenderMode): void {
    // Record the explicit choice first so the zoom watcher (if it ever re-runs)
    // can bail out instead of flipping featureReduction back.
    this.currentRenderMode = mode;

    if (!this.layer) {
      return;
    }

    switch (mode) {
      case 'pins':
        // 'pins' in the simplified UI means "restore default behavior" — that
        // includes the zoom-driven auto-clustering watcher. Restore both the
        // renderer and the watcher so toggling heatmap OFF gives the user back
        // the same tight per-location cluster bubbles they started with.
        this.layer.renderer = this.buildPinRenderer(this.lastSymbolData);
        this.layer.featureReduction = this.getClusteringConfig() as any;
        if (this.currentMapView) {
          this.setupClusteringZoomWatcher(this.currentMapView);
        }
        break;

      case 'clustered':
        this.layer.renderer = this.buildPinRenderer(this.lastSymbolData);
        this.layer.featureReduction = this.getClusteringConfig() as any;
        this.teardownClusteringZoomWatcher();
        break;

      case 'heatmap':
        this.layer.renderer = this.buildHeatmapRenderer() as any;
        this.layer.featureReduction = null as any;
        this.teardownClusteringZoomWatcher();
        break;
    }
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  /**
   * Tear down the zoom watcher that toggles clustering on/off.
   */
  private teardownClusteringZoomWatcher(): void {
    if (this.clusteringZoomWatchHandle) {
      this.clusteringZoomWatchHandle.remove();
      this.clusteringZoomWatchHandle = null;
    }
  }

  /**
   * Build a heatmap renderer weighted by the `count` attribute.
   * Colors ramp from transparent → yellow → orange → red as density increases.
   */
  private buildHeatmapRenderer(): __esri.HeatmapRenderer {
    return {
      type: 'heatmap',
      field: 'count',
      radius: 18,
      minDensity: 0,
      maxDensity: 0.04,
      colorStops: [
        { ratio: 0,    color: 'rgba(0, 0, 0, 0)' },
        { ratio: 0.2,  color: 'rgba(255, 235, 59, 0.6)' },
        { ratio: 0.5,  color: 'rgba(255, 152, 0, 0.85)' },
        { ratio: 1,    color: 'rgba(244, 67, 54, 1)' },
      ],
    } as unknown as __esri.HeatmapRenderer;
  }

  /**
   * Remove the layer and cleanup
   */
  removeLayer(mapView: __esri.MapView): void {
    this.cleanupHandlers();

    if (this.layer) {
      mapView.map.remove(this.layer);
      this.layer = null;
    }

    this.previousMarkerUuids.clear();
    this.previousMarkersHash = '';
    this.previousSymbolsHadCIM = false;
    this.markerPicturesMap.clear();
    this.multiItemLocationsMap.clear();
  }

  // ============================================================================
  // Label Methods
  // ============================================================================

  /**
   * Apply label configuration to the unified markers layer
   */
  applyLabels(labelConfig: MapLabelConfig): void {
    if (!this.layer) return;

    if (labelConfig.enabled) {
      this.layer.labelingInfo = this.buildLabelingInfo(labelConfig.labelBy) as any;
      this.layer.labelsVisible = true;
    } else {
      this.layer.labelsVisible = false;
    }
  }

  /**
   * Create/update an invisible FeatureLayer with point features at as-built geometry centroids.
   * This layer exists purely for ESRI's native label engine — no visible markers.
   */
  async updateAsBuiltLabelLayer(
    mapView: __esri.MapView,
    geometries: any[],
    labelConfig: MapLabelConfig
  ): Promise<void> {
    this.labelConfigCache = labelConfig;
    const asBuiltGeometries = geometries.filter(g => !g.isSiteGeometry && (g.bid_item_name || g.bid_item_code));

    if (asBuiltGeometries.length === 0 || !labelConfig.enabled) {
      this.removeAsBuiltLabelLayer(mapView);
      return;
    }

    // For name/code/site: aggregate by bid item (label text is identical across pieces, so one per bid item).
    // For quantity: one label PER geometry (each piece has its own quantity, a distinct data point).
    type GroupEntry = {
      geoms: any[];
      anchorLng: number;
      anchorLat: number;
      anchorSize: number;
      centroidSumLng: number;
      centroidSumLat: number;
      centroidCount: number;
    };
    const groups = new Map<string, GroupEntry>();
    // Embedded viewer (aggregateQuantityPerBidItem): every piece of a bid item
    // carries the SAME effective rolled-up quantity, so per-geometry would repeat
    // it. Group by bid item instead and anchor at the combined centroid (averaged
    // below). Default (main map) quantity stays one-label-per-piece.
    const aggregatePerBidItem =
      labelConfig.labelBy === 'quantity' && labelConfig.aggregateQuantityPerBidItem === true;
    const perGeom = labelConfig.labelBy === 'quantity' && !aggregatePerBidItem;

    let keyCounter = 0;
    for (const geom of asBuiltGeometries) {
      const centroid = this.calculateCentroid(geom);
      if (!centroid) continue;
      const size = this.estimateGeometryBboxArea(geom);
      const key = perGeom
        ? `g${keyCounter++}`
        : `${geom.bid_item_code || ''}__${geom.bid_item_name || ''}__${geom.site_name || ''}`;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          geoms: [geom],
          anchorLng: centroid.longitude,
          anchorLat: centroid.latitude,
          anchorSize: size,
          centroidSumLng: centroid.longitude,
          centroidSumLat: centroid.latitude,
          centroidCount: 1,
        });
      } else {
        existing.geoms.push(geom);
        existing.centroidSumLng += centroid.longitude;
        existing.centroidSumLat += centroid.latitude;
        existing.centroidCount += 1;
        if (size > existing.anchorSize) {
          existing.anchorLng = centroid.longitude;
          existing.anchorLat = centroid.latitude;
          existing.anchorSize = size;
        }
      }
    }

    // For the aggregated bid-item mode, anchor at the mean of all member centroids
    // (the combined center of the bid item's shapes) rather than the largest piece.
    if (aggregatePerBidItem) {
      for (const entry of Array.from(groups.values())) {
        entry.anchorLng = entry.centroidSumLng / entry.centroidCount;
        entry.anchorLat = entry.centroidSumLat / entry.centroidCount;
      }
    }

    // Ensure a text-label graphics layer exists on the map
    if (!this.asBuiltTextLabelLayer) {
      this.asBuiltTextLabelLayer = new GraphicsLayer({ id: 'asBuiltTextLabels' as any, listMode: 'hide' as any });
      mapView.map.add(this.asBuiltTextLabelLayer);
    }
    this.asBuiltTextLabelLayer.removeAll();
    this.labelCandidates = [];

    // Build one text Graphic per aggregated bid item
    for (const { geoms, anchorLng, anchorLat, anchorSize } of Array.from(groups.values())) {
      // Aggregated bid-item mode: every piece carries the same effective quantity,
      // so take it once (max guards against any stray 0). Otherwise sum the pieces.
      const totalQty = aggregatePerBidItem
        ? geoms.reduce((m, g) => Math.max(m, Number(g.quantity) || 0), 0)
        : geoms.reduce((s, g) => s + (Number(g.quantity) || 0), 0);
      const first = geoms[0];
      const text = this.formatAsBuiltLabelText(
        {
          bid_item_name: first.bid_item_name || '',
          bid_item_code: first.bid_item_code || '',
          bid_item_unit: first.bid_item_unit || '',
          quantity: totalQty,
          site_name: first.site_name || '',
        },
        labelConfig.labelBy
      );
      if (!text) continue;

      // Priority: quantity if available, else geometry size (larger feature = more important)
      const priority = totalQty > 0 ? totalQty : anchorSize * 1e8;

      const graphic = new Graphic({
        geometry: new Point({ longitude: anchorLng, latitude: anchorLat, spatialReference: { wkid: 4326 } }),
        symbol: {
          // @ts-ignore autocast
          type: 'text',
          color: '#060B1E',
          haloColor: [255, 255, 255, 1],
          haloSize: 2,
          yoffset: 8,
          font: { family: 'Montserrat', size: 10, weight: 'bold' },
          text,
        },
        visible: true,
      });

      this.asBuiltTextLabelLayer.add(graphic);
      // Rough text width estimate at 10pt bold: ~6.5px per char; height ~14px including halo
      this.labelCandidates.push({
        graphic,
        longitude: anchorLng,
        latitude: anchorLat,
        text,
        priority,
        widthPx: Math.min(text.length * 6.5, 220),
        heightPx: 14,
      });
    }

    // Bind viewpoint changes so labels re-deconflict on pan/zoom
    if (this.labelViewpointHandle) {
      this.labelViewpointHandle.remove?.();
      this.labelViewpointHandle = null;
    }
    this.labelViewpointHandle = reactiveUtils.watch(
      () => [mapView.stationary, mapView.scale, mapView.extent],
      () => {
        if (mapView.stationary) this.recomputeLabelVisibility(mapView);
      }
    );

    this.recomputeLabelVisibility(mapView);
  }

  /**
   * Build label text from an aggregated bid item record using the active label-by mode.
   */
  private formatAsBuiltLabelText(
    row: { bid_item_name: string; bid_item_code: string; bid_item_unit: string; quantity: number; site_name: string },
    labelBy: LabelByOption
  ): string {
    switch (labelBy) {
      case 'bid_item_name':
        return row.bid_item_name || '';
      case 'bid_item_code':
        return row.bid_item_code || '';
      case 'site_name':
        return row.site_name || '';
      case 'quantity': {
        if (!(row.quantity > 0)) return '';
        const rounded = row.quantity >= 100 ? Math.round(row.quantity).toString() : row.quantity.toFixed(2);
        return row.bid_item_unit ? `${rounded} ${row.bid_item_unit}` : rounded;
      }
      default:
        return row.bid_item_name || '';
    }
  }

  /**
   * Greedy label placement with screen-space collision detection.
   * Higher priority labels win; overlapping lower-priority labels are hidden.
   * Runs on every pan/zoom — zooming in naturally reveals more labels.
   */
  private recomputeLabelVisibility(mapView: __esri.MapView): void {
    if (!this.labelCandidates.length || !this.asBuiltTextLabelLayer) return;

    const sorted = [...this.labelCandidates].sort((a, b) => b.priority - a.priority);
    const placed: Array<{ xmin: number; xmax: number; ymin: number; ymax: number }> = [];
    const pad = 4;

    for (const c of sorted) {
      const screen = mapView.toScreen(c.graphic.geometry as __esri.Point);
      if (!screen || Number.isNaN(screen.x) || Number.isNaN(screen.y)) {
        c.graphic.visible = false;
        continue;
      }
      // Text is drawn above the point (yoffset=8) so bbox sits just above the centroid
      const halfW = c.widthPx / 2 + pad;
      const box = {
        xmin: screen.x - halfW,
        xmax: screen.x + halfW,
        ymin: screen.y - c.heightPx - 12 - pad,
        ymax: screen.y - 12 + pad,
      };
      const collides = placed.some(p =>
        !(box.xmax < p.xmin || box.xmin > p.xmax || box.ymax < p.ymin || box.ymin > p.ymax)
      );
      c.graphic.visible = !collides;
      if (!collides) placed.push(box);
    }
  }

  /**
   * Apply label config to the as-built centroid label layer
   */
  applyAsBuiltLabels(labelConfig: MapLabelConfig): void {
    this.labelConfigCache = labelConfig;
    if (!this.asBuiltTextLabelLayer) return;
    this.asBuiltTextLabelLayer.visible = labelConfig.enabled;
  }

  /**
   * Remove the as-built centroid label layer from the map
   */
  removeAsBuiltLabelLayer(mapView: __esri.MapView): void {
    if (this.labelViewpointHandle) {
      this.labelViewpointHandle.remove?.();
      this.labelViewpointHandle = null;
    }
    if (this.asBuiltTextLabelLayer) {
      mapView.map.remove(this.asBuiltTextLabelLayer);
      this.asBuiltTextLabelLayer = null;
    }
    this.labelCandidates = [];
    // Keep legacy asBuiltLabelLayer removal for backwards-compat
    if (this.asBuiltLabelLayer) {
      mapView.map.remove(this.asBuiltLabelLayer);
      this.asBuiltLabelLayer = null;
    }
  }

  /**
   * Build labeling info array with Arcade expressions per marker type
   */
  buildLabelingInfo(labelBy: LabelByOption): any[] {
    // Figma spec: white bg, #E9ECF4 border, Montserrat SemiBold 12px, #060B1E text
    const baseLabelSymbol = {
      type: 'text',
      color: '#060B1E',
      backgroundColor: [255, 255, 255, 1],
      borderLineColor: [233, 236, 244, 1],
      borderLineSize: 0.75,
      haloColor: [255, 255, 255, 0.9],
      haloSize: 0.5,
      font: { family: 'Montserrat', size: 11, weight: 'semi-bold' },
      yoffset: -10
    };

    const labelClasses: any[] = [];

    // Only show labels on highlighted markers (is_hovered_site = 1);
    // faded markers (is_hovered_site = 0) get their labels hidden
    const hoveredFilter = ' AND is_hovered_site = 1';

    // Bid Item labels
    labelClasses.push({
      where: "markerType = 'bid_item'" + hoveredFilter,
      labelPlacement: 'below-center',
      labelExpressionInfo: { expression: this.getLabelExpression('bid_item', labelBy) },
      symbol: { ...baseLabelSymbol },
      minScale: 50000,
      deconflictionStrategy: 'static'
    });

    // Activity labels
    labelClasses.push({
      where: "markerType = 'activity'" + hoveredFilter,
      labelPlacement: 'below-center',
      labelExpressionInfo: { expression: this.getLabelExpression('activity', labelBy) },
      symbol: { ...baseLabelSymbol },
      minScale: 50000,
      deconflictionStrategy: 'static'
    });

    // Delivery labels
    labelClasses.push({
      where: "markerType = 'delivery'" + hoveredFilter,
      labelPlacement: 'below-center',
      labelExpressionInfo: { expression: this.getLabelExpression('delivery', labelBy) },
      symbol: { ...baseLabelSymbol },
      minScale: 50000,
      deconflictionStrategy: 'static'
    });

    // Picture labels (slightly smaller)
    labelClasses.push({
      where: "markerType = 'picture'" + hoveredFilter,
      labelPlacement: 'below-center',
      labelExpressionInfo: { expression: this.getLabelExpression('picture', labelBy) },
      symbol: { ...baseLabelSymbol, font: { ...baseLabelSymbol.font, size: 10 } },
      minScale: 25000,
      deconflictionStrategy: 'static'
    });

    return labelClasses;
  }

  /**
   * Get Arcade label expression based on marker type and label mode
   */
  private getLabelExpression(markerType: string, labelBy: LabelByOption): string {
    switch (labelBy) {
      case 'bid_item_name':
        if (markerType === 'bid_item' || markerType === 'delivery') return '$feature.bid_item_name';
        if (markerType === 'activity') return '$feature.title';
        if (markerType === 'picture') return '$feature.source_name';
        return '$feature.title';
      case 'quantity':
        if (markerType === 'bid_item' || markerType === 'delivery')
          return "IIF($feature.quantity > 0, Text($feature.quantity, '#,##0.##') + ' ' + $feature.bid_item_unit, '')";
        return '$feature.title';
      case 'bid_item_code':
        if (markerType === 'bid_item' || markerType === 'delivery') return '$feature.bid_item_code';
        if (markerType === 'activity') return '$feature.title';
        return '$feature.source_name';
      case 'site_name':
        return '$feature.site_name';
      case 'title':
        return '$feature.title';
      default:
        return '$feature.title';
    }
  }

  private buildAsBuiltLabelingInfo(labelBy: LabelByOption): any[] {
    // Text-only labels with a strong white halo (no background box).
    // Smaller visual footprint → ArcGIS deconfliction can place more of them
    // without overlap, keeping the map readable at dense work areas.
    const baseLabelSymbol = {
      type: 'text',
      color: '#060B1E',
      haloColor: [255, 255, 255, 1],
      haloSize: 2,
      font: { family: 'Montserrat', size: 10, weight: 'bold' },
    };

    const expression = this.getAsBuiltLabelExpression(labelBy);

    return [{
      labelPlacement: 'above-center',
      labelExpressionInfo: { expression },
      symbol: baseLabelSymbol,
      minScale: 10000,
      deconflictionStrategy: 'static',
      removeDuplicates: 'all',
      removeDuplicatesDistance: 0,
      repeatLabel: false,
    }];
  }

  private getAsBuiltLabelExpression(labelBy: LabelByOption): string {
    switch (labelBy) {
      case 'bid_item_name':
        return '$feature.bid_item_name';
      case 'quantity':
        return "IIF($feature.quantity > 0, Text($feature.quantity, '#,##0.##') + ' ' + $feature.bid_item_unit, '')";
      case 'bid_item_code':
        return '$feature.bid_item_code';
      case 'site_name':
        return '$feature.site_name';
      case 'title':
        return '$feature.title';
      default:
        return '$feature.bid_item_name';
    }
  }

  /**
   * Quick bbox-area estimate in (degree²) for a GeoJSON-style geometry.
   * Used to rank which geometry in an aggregate is "biggest" so labels can
   * be anchored to the most visible shape rather than a floating midpoint.
   */
  private estimateGeometryBboxArea(geometry: any): number {
    const coords = geometry?.coordinates;
    if (!coords) return 0;
    let flat: number[][] = [];
    switch (geometry.type) {
      case 'Point':
        return 0;
      case 'LineString':
      case 'MultiPoint':
        flat = coords;
        break;
      case 'Polygon':
        flat = coords[0] || [];
        break;
      case 'MultiLineString':
        flat = ([] as number[][]).concat(...(coords as number[][][]));
        break;
      case 'MultiPolygon':
        flat = ([] as number[][]).concat(...(coords as number[][][][]).map(p => p[0] || []));
        break;
      default:
        return 0;
    }
    if (flat.length === 0) return 0;
    let minX = flat[0][0], maxX = flat[0][0], minY = flat[0][1], maxY = flat[0][1];
    for (const [x, y] of flat) {
      if (x < minX) minX = x; else if (x > maxX) maxX = x;
      if (y < minY) minY = y; else if (y > maxY) maxY = y;
    }
    return (maxX - minX) * (maxY - minY);
  }

  private calculateCentroid(geometry: any): { latitude: number; longitude: number } | null {
    const coords = geometry.coordinates;
    if (!coords) return null;

    let points: number[][] = [];

    switch (geometry.type) {
      case 'Point':
        return { longitude: coords[0], latitude: coords[1] };
      case 'LineString':
        points = coords;
        break;
      case 'Polygon':
        points = coords[0] || [];
        break;
      case 'MultiPoint':
        points = coords;
        break;
      case 'MultiLineString':
        points = coords.flat();
        break;
      case 'MultiPolygon':
        points = coords.map((poly: number[][][]) => poly[0] || []).flat();
        break;
      default:
        return null;
    }

    if (points.length === 0) return null;

    const sum = points.reduce(
      (acc, pt) => ({ lng: acc.lng + pt[0], lat: acc.lat + pt[1] }),
      { lng: 0, lat: 0 }
    );

    return {
      longitude: sum.lng / points.length,
      latitude: sum.lat / points.length,
    };
  }

  // ============================================================================
  // Private Methods - Symbol Creation
  // ============================================================================

  /**
   * Create symbols for multi-item location markers
   */
  private createMultiItemSymbols(
    groups: Array<{ location: string; data: LocationGroup }>,
    itemIdToSiteId: Map<string, string>
  ): MarkerSymbolData[] {
    return groups.map(group => {
      const items = group.data;
      const totalCount = items.pictures.length + items.bidItems.length +
                         items.activities.length + items.deliveries.length + items.forms.length;

      // Calculate average coordinates
      const allCoords: Array<{ lat: number; lng: number }> = [];

      items.pictures.forEach(pic => {
        if (pic.latitude != null && pic.longitude != null) {
          allCoords.push({ lat: parseFloat(pic.latitude as any), lng: parseFloat(pic.longitude as any) });
        }
      });

      items.bidItems.forEach(item => {
        const lat = item.geometry?.latitude ?? item.data?.geometry?.latitude;
        const lng = item.geometry?.longitude ?? item.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          allCoords.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
      });

      items.activities.forEach(item => {
        const lat = item.geometry?.latitude ?? item.data?.geometry?.latitude;
        const lng = item.geometry?.longitude ?? item.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          allCoords.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
      });

      items.deliveries.forEach(item => {
        const lat = item.geometry?.latitude ?? item.data?.geometry?.latitude;
        const lng = item.geometry?.longitude ?? item.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          allCoords.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
      });

      items.forms.forEach(item => {
        const lat = item.geometry?.latitude ?? item.data?.geometry?.latitude;
        const lng = item.geometry?.longitude ?? item.data?.geometry?.longitude;
        if (lat != null && lng != null) {
          allCoords.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
      });

      if (allCoords.length === 0) {
        return null!;
      }

      const lat = allCoords.reduce((sum, c) => sum + c.lat, 0) / allCoords.length;
      const lng = allCoords.reduce((sum, c) => sum + c.lng, 0) / allCoords.length;

      const markerUuid = `multi-item-${group.location}`;
      this.multiItemLocationsMap.set(markerUuid, items);

      // Create CIM symbol
      const countText = totalCount > 9 ? '9+' : totalCount.toString();
      const cimSymbol = new CIMSymbol({
        data: {
          type: 'CIMSymbolReference',
          symbol: {
            type: 'CIMPointSymbol',
            symbolLayers: [
              {
                type: 'CIMVectorMarker',
                enable: true,
                size: 40,
                frame: { xmin: 0, ymin: 0, xmax: 40, ymax: 40 },
                markerGraphics: [{
                  type: 'CIMMarkerGraphic',
                  geometry: {
                    rings: [this.mapSymbolFactory.generateCircleCoordinates(20, 20, 20, 32)]
                  },
                  symbol: {
                    type: 'CIMPolygonSymbol',
                    symbolLayers: [
                      { type: 'CIMSolidFill', enable: true, color: [16, 54, 214, 255] },
                      { type: 'CIMSolidStroke', enable: true, color: [101, 131, 255, 255], width: 2 }
                    ]
                  } as any
                }]
              },
              {
                type: 'CIMVectorMarker',
                enable: true,
                size: 14,
                frame: { xmin: 0, ymin: 0, xmax: 14, ymax: 14 },
                markerGraphics: [{
                  type: 'CIMMarkerGraphic',
                  geometry: { x: 7, y: 7 },
                  symbol: {
                    type: 'CIMTextSymbol',
                    fontFamilyName: 'Montserrat',
                    fontStyleName: 'SemiBold',
                    height: 14,
                    horizontalAlignment: 'Center',
                    verticalAlignment: 'Center',
                    symbol: {
                      type: 'CIMPolygonSymbol',
                      symbolLayers: [{ type: 'CIMSolidFill', enable: true, color: [255, 255, 255, 255] }]
                    }
                  } as any,
                  textString: countText
                }]
              }
            ]
          }
        }
      });

      // Get site ID and created_by_id from first item
      const firstItem = items.pictures[0] || items.bidItems[0] || items.activities[0] || items.deliveries[0] || items.forms[0];
      const firstItemSiteId = firstItem?.siteId ||
        firstItem?.site_id ||
        firstItem?.site_uuid ||
        firstItem?.bid_item?.site_id ||
        firstItem?.bid_item?.site?.id ||
        (firstItem?.id ? itemIdToSiteId.get(firstItem.id) : null) ||
        (firstItem?.data?.id ? itemIdToSiteId.get(firstItem.data.id) : null) ||
        null;

      // Get created_by_id from first item with one (for inspector hover filtering)
      const firstItemWithCreator = [...items.bidItems, ...items.activities, ...items.deliveries, ...items.forms]
        .find(item => item.data?.created_by_id);
      const createdById = firstItemWithCreator?.data?.created_by_id || null;

      const bidItemIds = items.bidItems.map(item => item.data?.bid_item?.id).filter(Boolean);

      return {
        uuid: markerUuid,
        symbol: cimSymbol as any,
        attributes: {
          is_multi_item_location: 1,
          markerType: 'multi_item_location',
          count: totalCount,
          location_key: group.location,
          latitude: lat,
          longitude: lng,
          site_id: firstItemSiteId,
          created_by_id: createdById,
          is_hovered_site: 1,
          pictures_count: items.pictures.length,
          bid_items_count: items.bidItems.length,
          activities_count: items.activities.length,
          deliveries_count: items.deliveries.length,
          forms_count: items.forms.length,
          bid_item_ids: bidItemIds.join(',')
        },
        markerType: 'multi_item_location'
      };
    }).filter(Boolean);
  }

  /**
   * Create symbols for same-location picture groups
   */
  private async createSameLocationPictureSymbols(
    groups: Array<{ location: string; pictures: MarkerItem[] }>,
    itemIdToSiteId: Map<string, string>,
    reportBidItemToBidItemId: Map<string, string>
  ): Promise<MarkerSymbolData[]> {
    const promises = groups.map(async (group) => {
      try {
        const firstPicture = group.pictures[0];
        const imageUrl = firstPicture.thumb_url || firstPicture.original_url;
        const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl!, 70);

        // Check if all pictures have same direction
        const allHaveDirection = group.pictures.every(p =>
          p.direction !== null && p.direction !== undefined
        );
        let allSameDirection = false;
        let commonDirection = 0;

        if (allHaveDirection) {
          const firstDirection = group.pictures[0].direction!;
          allSameDirection = group.pictures.every(p =>
            Math.abs(p.direction! - firstDirection) <= 5
          );
          if (allSameDirection) {
            commonDirection = firstDirection;
          }
        }

        const showArrow = allHaveDirection && allSameDirection;
        const markerUuid = `same-location-${group.location}`;

        this.markerPicturesMap.set(markerUuid, group.pictures);

        // Get bid_item_id
        let bidItemId: string | null = null;
        let reportBidItemId: string | null = null;
        if (firstPicture.source_type === 'report_bid_item' && firstPicture.source_uuid) {
          reportBidItemId = firstPicture.source_uuid;
          bidItemId = reportBidItemToBidItemId.get(firstPicture.source_uuid) || null;
        }

        return {
          uuid: markerUuid,
          symbol: this.mapSymbolFactory.createSameLocationMarkerSymbol(
            circularImageUrl,
            showArrow ? commonDirection : 0,
            group.pictures.length,
            markerUuid,
            showArrow
          ),
          attributes: {
            is_same_location: 1,
            count: group.pictures.length,
            picture_uuids: group.pictures.map(p => p.uuid).join(','),
            site_id: firstPicture.site_uuid ||
              firstPicture.siteId ||
              firstPicture.site_id ||
              firstPicture.bid_item?.site_id ||
              firstPicture.bid_item?.site?.id ||
              (reportBidItemId ? itemIdToSiteId.get(reportBidItemId) : null) ||
              (bidItemId ? itemIdToSiteId.get(bidItemId) : null) ||
              null,
            is_hovered_site: 1,
            report_bid_item_id: reportBidItemId,
            bid_item_id: bidItemId,
            latitude: parseFloat(firstPicture.latitude as any),
            longitude: parseFloat(firstPicture.longitude as any),
            ...firstPicture,
            title: (firstPicture as any).source_name || '',
            site_name: (firstPicture as any).site_name || ''
          },
          markerType: 'picture'
        };
      } catch (error) {
        console.error('Failed to process same-location group', group.location, error);
        return null;
      }
    });

    const results = await Promise.all(promises);
    return results.filter(Boolean) as MarkerSymbolData[];
  }

  /**
   * Create symbols for individual pictures
   */
  private async createIndividualPictureSymbols(
    pictures: MarkerItem[],
    itemIdToSiteId: Map<string, string>,
    reportBidItemToBidItemId: Map<string, string>
  ): Promise<MarkerSymbolData[]> {
    const promises = pictures.map(async (picture) => {
      try {
        const imageUrl = picture.thumb_url || picture.original_url;
        const circularImageUrl = await this.imageProcessingService.createCircularImage(imageUrl!, 70);

        this.markerPicturesMap.set(picture.uuid!, [picture]);

        const hasValidDirection = picture.direction !== null && picture.direction !== undefined;
        const showArrow = hasValidDirection;
        const rotation = hasValidDirection ? picture.direction! : 0;

        let bidItemId: string | null = null;
        let reportBidItemId: string | null = null;
        if (picture.source_type === 'report_bid_item' && picture.source_uuid) {
          reportBidItemId = picture.source_uuid;
          bidItemId = reportBidItemToBidItemId.get(picture.source_uuid) || null;
        }

        return {
          uuid: picture.uuid!,
          symbol: this.mapSymbolFactory.createPictureMarkerSymbol(circularImageUrl, rotation, picture.uuid!, showArrow),
          attributes: {
            is_same_location: 0,
            count: 1,
            site_id: picture.site_uuid ||
              picture.siteId ||
              picture.site_id ||
              picture.bid_item?.site_id ||
              picture.bid_item?.site?.id ||
              (reportBidItemId ? itemIdToSiteId.get(reportBidItemId) : null) ||
              (bidItemId ? itemIdToSiteId.get(bidItemId) : null) ||
              null,
            is_hovered_site: 1,
            report_bid_item_id: reportBidItemId,
            bid_item_id: bidItemId,
            latitude: parseFloat(picture.latitude as any),
            longitude: parseFloat(picture.longitude as any),
            ...picture,
            title: (picture as any).source_name || '',
            site_name: (picture as any).site_name || ''
          },
          markerType: 'picture'
        };
      } catch (error) {
        console.error('Failed to process picture', picture.uuid, error);
        return this.createPlaceholderPictureSymbol(picture, itemIdToSiteId, reportBidItemToBidItemId);
      }
    });

    const results = await Promise.all(promises);
    return results.filter(Boolean) as MarkerSymbolData[];
  }

  /**
   * Create placeholder symbol for failed picture
   */
  private createPlaceholderPictureSymbol(
    picture: MarkerItem,
    itemIdToSiteId: Map<string, string>,
    reportBidItemToBidItemId: Map<string, string>
  ): MarkerSymbolData {
    let bidItemId: string | null = null;
    let reportBidItemId: string | null = null;
    if (picture.source_type === 'report_bid_item' && picture.source_uuid) {
      reportBidItemId = picture.source_uuid;
      bidItemId = reportBidItemToBidItemId.get(picture.source_uuid) || null;
    }

    return {
      uuid: picture.uuid!,
      symbol: this.mapSymbolFactory.createPlaceholderMarkerSymbol(),
      attributes: {
        is_same_location: 0,
        count: 1,
        site_id: picture.site_uuid ||
          picture.siteId ||
          picture.site_id ||
          picture.bid_item?.site_id ||
          picture.bid_item?.site?.id ||
          null,
        is_hovered_site: 1,
        report_bid_item_id: reportBidItemId,
        bid_item_id: bidItemId,
        latitude: parseFloat(picture.latitude as any),
        longitude: parseFloat(picture.longitude as any),
        ...picture
      },
      markerType: 'picture'
    };
  }

  /**
   * Create symbol for bid item marker
   */
  private createBidItemSymbol(
    marker: MarkerItem,
    itemIdToSiteId: Map<string, string>,
    colorConfig?: { viewMode: 'site' | 'bid_item' | 'day' | 'inspector'; colorMap: { [key: string]: string } },
    coloredMarkerUrlMap?: Map<string, { bid_item: string; activity: string; delivery: string }>
  ): MarkerSymbolData {
    // Resolve site_id for this marker
    const siteId = marker.data?.siteId ||
      marker.data?.site_id ||
      marker.data?.site_uuid ||
      marker.data?.bid_item?.site_id ||
      marker.data?.bid_item?.site?.id ||
      (marker.data?.id ? itemIdToSiteId.get(marker.data.id) : null) ||
      (marker.data?.bid_item?.id ? itemIdToSiteId.get(marker.data.bid_item.id) : null) ||
      null;

    // Resolve bid_item_id for this marker
    const bidItemId = marker.data?.bid_item?.id || marker.data?.bid_item_id || null;

    // Get colored marker URL based on view mode
    let markerUrl = '/assets/icons/map/markers/bid_items@4x.png';
    if (colorConfig?.colorMap && coloredMarkerUrlMap) {
      let lookupKey: string | null = null;

      // Determine lookup key based on view mode
      switch (colorConfig.viewMode) {
        case 'site':
          lookupKey = siteId;
          break;
        case 'bid_item':
          lookupKey = bidItemId;
          break;
        case 'day':
          // Use reportDate (camelCase) - this is the format from marker data
          lookupKey = marker.data?.reportDate || marker.data?.report_date || null;
          break;
        case 'inspector':
          lookupKey = marker.data?.created_by_id || marker.data?.createdById || marker.data?.inspector_id || null;
          break;
      }

      if (lookupKey) {
        const color = colorConfig.colorMap[lookupKey];
        if (color) {
          const coloredUrls = coloredMarkerUrlMap.get(color);
          if (coloredUrls?.bid_item) {
            markerUrl = coloredUrls.bid_item;
          }
        }
      }
    }

    return {
      uuid: marker.data?.id || `bid_${Math.random()}`,
      symbol: {
        type: 'picture-marker',
        url: markerUrl,
        width: '26px',
        height: '36px'
      },
      attributes: {
        ...marker.data,
        longitude: marker.geometry?.longitude,
        latitude: marker.geometry?.latitude,
        count: 1,
        site_id: siteId,
        is_hovered_site: 1,
        report_bid_item_id: marker.data?.id || null,
        bid_item_id: bidItemId,
        bid_item_name: marker.data?.bid_item?.description || marker.data?.bid_item?.item || '',
        bid_item_code: marker.data?.bid_item?.item || '',
        bid_item_unit: marker.data?.bid_item?.unit || '',
        quantity: marker.data?.quantity || 0,
        title: marker.data?.bid_item?.description || '',
        site_name: marker.data?.siteName || ''
      },
      markerType: 'bid_item'
    };
  }

  /**
   * Create symbol for activity marker
   */
  private createActivitySymbol(
    marker: MarkerItem,
    itemIdToSiteId: Map<string, string>,
    colorConfig?: { viewMode: 'site' | 'bid_item' | 'day' | 'inspector'; colorMap: { [key: string]: string } },
    coloredMarkerUrlMap?: Map<string, { bid_item: string; activity: string; delivery: string }>
  ): MarkerSymbolData {
    // Resolve site_id for this marker
    const siteId = marker.data?.siteId ||
      marker.data?.site_id ||
      marker.data?.site_uuid ||
      marker.data?.bid_item?.site_id ||
      marker.data?.bid_item?.site?.id ||
      (marker.data?.id ? itemIdToSiteId.get(marker.data.id) : null) ||
      null;

    // Get colored marker URL based on view mode
    let markerUrl = '/assets/icons/map/markers/activities@4x.png';
    if (colorConfig?.colorMap && coloredMarkerUrlMap) {
      let lookupKey: string | null = null;

      // Determine lookup key based on view mode
      switch (colorConfig.viewMode) {
        case 'site':
          lookupKey = siteId;
          break;
        case 'bid_item':
          // Activities don't have bid_item association, use site as fallback
          lookupKey = siteId;
          break;
        case 'day':
          // Use reportDate (camelCase) - this is the format from marker data
          lookupKey = marker.data?.reportDate || marker.data?.report_date || null;
          break;
        case 'inspector':
          lookupKey = marker.data?.created_by_id || marker.data?.createdById || marker.data?.inspector_id || null;
          break;
      }

      if (lookupKey) {
        const color = colorConfig.colorMap[lookupKey];
        if (color) {
          const coloredUrls = coloredMarkerUrlMap.get(color);
          if (coloredUrls?.activity) {
            markerUrl = coloredUrls.activity;
          }
        }
      }
    }

    return {
      uuid: marker.data?.id || `activity_${Math.random()}`,
      symbol: {
        type: 'picture-marker',
        url: markerUrl,
        width: '26px',
        height: '36px'
      },
      attributes: {
        ...marker.data,
        longitude: marker.geometry?.longitude,
        latitude: marker.geometry?.latitude,
        count: 1,
        site_id: siteId,
        is_hovered_site: 1,
        activity_id: marker.data?.id || null,
        title: marker.data?.title || marker.data?.name || '',
        site_name: marker.data?.siteName || ''
      },
      markerType: 'activity'
    };
  }

  /**
   * Create symbol for delivery marker
   */
  private createDeliverySymbol(
    marker: MarkerItem,
    itemIdToSiteId: Map<string, string>,
    colorConfig?: { viewMode: 'site' | 'bid_item' | 'day' | 'inspector'; colorMap: { [key: string]: string } },
    coloredMarkerUrlMap?: Map<string, { bid_item: string; activity: string; delivery: string }>
  ): MarkerSymbolData {
    // Resolve site_id for this marker
    const siteId = marker.data?.siteId ||
      marker.data?.site_id ||
      marker.data?.site_uuid ||
      marker.data?.bid_item?.site_id ||
      marker.data?.bid_item?.site?.id ||
      (marker.data?.id ? itemIdToSiteId.get(marker.data.id) : null) ||
      null;

    // Resolve bid_item_id for this marker (deliveries can be linked to bid items)
    const bidItemId = marker.data?.bid_item?.id || marker.data?.bid_item_id || null;

    // Get colored marker URL based on view mode
    let markerUrl = '/assets/map-markers/delivery-marker@4x.png';
    if (colorConfig?.colorMap && coloredMarkerUrlMap) {
      let lookupKey: string | null = null;

      // Determine lookup key based on view mode
      switch (colorConfig.viewMode) {
        case 'site':
          lookupKey = siteId;
          break;
        case 'bid_item':
          lookupKey = bidItemId || siteId; // Fallback to site if no bid item link
          break;
        case 'day':
          // Use reportDate (camelCase) - this is the format from marker data
          lookupKey = marker.data?.reportDate || marker.data?.report_date || null;
          break;
        case 'inspector':
          lookupKey = marker.data?.created_by_id || marker.data?.createdById || marker.data?.inspector_id || null;
          break;
      }

      if (lookupKey) {
        const color = colorConfig.colorMap[lookupKey];
        if (color) {
          const coloredUrls = coloredMarkerUrlMap.get(color);
          if (coloredUrls?.delivery) {
            markerUrl = coloredUrls.delivery;
          }
        }
      }
    }

    return {
      uuid: marker.data?.id || `delivery_${Math.random()}`,
      symbol: {
        type: 'picture-marker',
        url: markerUrl,
        width: '26px',
        height: '36px'
      },
      attributes: {
        ...marker.data,
        longitude: marker.geometry?.longitude,
        latitude: marker.geometry?.latitude,
        count: 1,
        site_id: siteId,
        is_hovered_site: 1,
        delivery_id: marker.data?.id || null,
        title: marker.data?.title || marker.data?.name || 'Material Delivery',
        bid_item_name: marker.data?.bid_item?.description || '',
        bid_item_code: marker.data?.bid_item?.item || '',
        bid_item_unit: marker.data?.bid_item?.unit || '',
        quantity: marker.data?.quantity || 0,
        site_name: marker.data?.siteName || ''
      },
      markerType: 'delivery'
    };
  }

  /**
   * Create symbol for control form marker.
   * Uses the configurable icon resolved from controlForm.ui_config.map_display.icon
   * (falls back to the default quality_test icon). Forms have no bid item link,
   * so they are not colorized by view mode (mirroring the simplest marker path).
   */
  private createFormSymbol(
    marker: MarkerItem,
    itemIdToSiteId: Map<string, string>
  ): MarkerSymbolData {
    // Resolve site_id for this marker
    const siteId = marker.data?.siteId ||
      marker.data?.site_id ||
      marker.data?.site_uuid ||
      (marker.data?.id ? itemIdToSiteId.get(marker.data.id) : null) ||
      null;

    // Resolve the configurable form icon from ui_config
    const markerConfig = this.mapSymbolFactory.getControlFormMarkerConfig(marker.data?.control_form);

    return {
      uuid: marker.data?.id || `form_${Math.random()}`,
      symbol: {
        type: 'picture-marker',
        url: markerConfig.url,
        width: markerConfig.width,
        height: markerConfig.height
      },
      attributes: {
        ...marker.data,
        longitude: marker.geometry?.longitude,
        latitude: marker.geometry?.latitude,
        count: 1,
        site_id: siteId,
        is_hovered_site: 1,
        form_id: marker.data?.id || null,
        title: marker.data?.title || marker.data?.control_form?.name || 'Form',
        site_name: marker.data?.siteName || ''
      },
      markerType: 'form'
    };
  }

  // ============================================================================
  // Private Methods - Layer Management
  // ============================================================================

  /**
   * Update existing layer with new features using diff-based applyEdits
   */
  private async updateExistingLayer(
    graphics: Graphic[],
    symbolData: MarkerSymbolData[],
    config: UnifiedLayerConfig,
    disableClustering: boolean = false
  ): Promise<void> {
    if (!this.layer) return;

    // Cache symbol data so setRenderMode can rebuild the pin renderer later.
    this.lastSymbolData = symbolData.map(s => ({ uuid: s.uuid, symbol: s.symbol }));

    // Update renderer — respect the current render mode so that marker redraws
    // (e.g. triggered by spatial-select filters or side-nav filter changes) do
    // NOT silently revert the user's explicit heatmap/clustered choice.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    if (this.currentRenderMode === 'heatmap') {
      this.layer.renderer = this.buildHeatmapRenderer() as any;
      this.layer.featureReduction = null as any;
    } else if (this.currentRenderMode === 'clustered') {
      this.layer.renderer = this.buildPinRenderer(this.lastSymbolData) as any;
      this.layer.featureReduction = this.getClusteringConfig() as any;
    } else {
      // 'pins' — default behavior; let the zoom watcher manage featureReduction.
      this.layer.renderer = this.buildPinRenderer(this.lastSymbolData) as any;
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // Update definition expression
    this.layer.definitionExpression = config.showPictures
      ? "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'picture' OR markerType = 'multi_item_location'"
      : "markerType = 'bid_item' OR markerType = 'activity' OR markerType = 'delivery' OR markerType = 'form' OR markerType = 'multi_item_location'";

    // Calculate diff
    const newUuids = new Set<string>(graphics.map(g => g.attributes.uuid));
    const toAdd = graphics.filter(g => !this.previousMarkerUuids.has(g.attributes.uuid));

    const toDeleteUuids: string[] = [];
    this.previousMarkerUuids.forEach(uuid => {
      if (!newUuids.has(uuid)) {
        toDeleteUuids.push(uuid);
      }
    });

    if (toAdd.length > 0 || toDeleteUuids.length > 0) {
      let deleteFeatures: Graphic[] = [];
      if (toDeleteUuids.length > 0) {
        try {
          const escapedUuids = toDeleteUuids.map(u => u.replace(/'/g, "''"));
          const whereClause = `uuid IN ('${escapedUuids.join("','")}')`;
          const queryResult = await this.layer.queryFeatures({
            where: whereClause,
            outFields: ['ObjectID']
          });
          deleteFeatures = queryResult.features;
        } catch (err) {
          console.error('Failed to query features for deletion:', err);
        }
      }

      try {
        await this.layer.applyEdits({
          deleteFeatures: deleteFeatures,
          addFeatures: toAdd
        });
      } catch (err) {
        console.error('applyEdits failed:', err);
      }
    }

    // Always refresh after renderer update to apply symbol changes (e.g., color changes from view mode switch)
    this.layer.refresh();

    this.previousMarkerUuids = newUuids;
  }

  /**
   * Create a new unified markers layer
   */
  private async createNewLayer(
    mapView: __esri.MapView,
    graphics: Graphic[],
    symbolData: MarkerSymbolData[],
    config: UnifiedLayerConfig,
    disableClustering: boolean = false
  ): Promise<void> {
    const currentZoom = mapView.zoom || 10;
    const shouldEnableClustering = !disableClustering && currentZoom < 21;

    // Cache symbol data + mapView so setRenderMode can rebuild/re-watch later.
    this.lastSymbolData = symbolData.map(s => ({ uuid: s.uuid, symbol: s.symbol }));
    this.currentMapView = mapView;

    this.layer = new FeatureLayer({
      id: 'unified-markers-layer',
      source: graphics,
      objectIdField: 'ObjectID',
      fields: UNIFIED_MARKER_FIELDS as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderer: this.buildPinRenderer(this.lastSymbolData) as any,
      visible: true,
      opacity: 1,
      featureReduction: shouldEnableClustering ? this.getClusteringConfig() : null
    });

    // If a non-pins render mode was explicitly chosen before this rebuild, apply
    // it on top of the freshly-created layer so the user's choice survives.
    /* eslint-disable @typescript-eslint/no-explicit-any */
    if (this.currentRenderMode === 'heatmap') {
      this.layer.renderer = this.buildHeatmapRenderer() as any;
      this.layer.featureReduction = null as any;
    } else if (this.currentRenderMode === 'clustered') {
      this.layer.featureReduction = this.getClusteringConfig() as any;
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // Add layer at the top (highest index) to ensure markers are above geometries
    mapView.map.add(this.layer, mapView.map.layers.length);
    const layerIndex = mapView.map.layers.indexOf(this.layer);
    await this.layer.when();

    // Wait for LayerView to be created and ready (ensures rendering is complete)
    // BUT don't wait forever - use a timeout to prevent hanging during map animations
    try {
      const layerView = await mapView.whenLayerView(this.layer);
      if (layerView.updating) {
        // Use Promise.race with a timeout to avoid infinite waiting during map animations
        const timeoutPromise = new Promise<void>(resolve => setTimeout(resolve, 500));
        await Promise.race([
          reactiveUtils.whenOnce(() => !layerView.updating),
          timeoutPromise
        ]);
      }
    } catch (err) {
      console.log('🟣 createNewLayer - LayerView creation failed:', err);
      // LayerView creation failed, continue anyway
    }

    // Force refresh to ensure markers are rendered immediately
    this.layer.refresh();

    this.previousMarkerUuids = new Set<string>(graphics.map(g => g.attributes.uuid));
    this.setupClusteringZoomWatcher(mapView);

    // If a non-pins render mode is active, tear down the zoom watcher we just
    // attached — those modes own featureReduction themselves and must not be
    // overridden by zoom-driven auto-clustering.
    if (this.currentRenderMode !== 'pins') {
      this.teardownClusteringZoomWatcher();
    }
  }

  /**
   * Enable clustering once the map becomes stationary.
   * This prevents cluster position calculation issues during animations.
   *
   * Uses requestAnimationFrame to defer the check to after pending animations
   * (like goTo) have started - checking synchronously would see stationary=true
   * before the animation microtask executes.
   */
  private enableClusteringWhenStationary(mapView: __esri.MapView): void {
    if (!this.layer) return;

    // Defer to next animation frame to ensure any pending goTo() animations have started
    // This is NOT a time-based delay - it's the proper way to wait for microtasks to execute
    requestAnimationFrame(() => {
      if (!this.layer) return;

      if (mapView.stationary) {
        // Map is truly stationary (no pending animations), enable clustering now
        this.layer.featureReduction = this.getClusteringConfig();
      } else {
        // Map is animating, wait for animation to complete
        reactiveUtils.whenOnce(() => mapView.stationary).then(() => {
          if (this.layer) {
            this.layer.featureReduction = this.getClusteringConfig();
          }
        });
      }
    });
  }

  /**
   * Setup zoom watcher for clustering enable/disable based on zoom level
   * At zoom >= 21, clustering is disabled to show individual markers
   */
  private setupClusteringZoomWatcher(mapView: __esri.MapView): void {
    // Tear down any prior watcher so we don't stack duplicates.
    this.teardownClusteringZoomWatcher();
    this.currentMapView = mapView;

    // Track current clustering state
    let clusteringEnabled = mapView.zoom < 21;

    this.clusteringZoomWatchHandle = reactiveUtils.watch(
      () => mapView.zoom,
      (currentZoom) => {
        if (!this.layer || currentZoom === undefined) return;

        // Belt-and-suspenders: if the user has made an explicit render-mode
        // choice, don't let zoom-driven auto-clustering override it. The
        // watcher is also torn down in those cases, so this should rarely
        // matter, but covers any race where a pending callback still fires.
        if (this.currentRenderMode !== 'pins') return;

        // Drilldown hides the other markers with a featureEffect filter, which a clustered layer ignores: clustering
        // on a zoom-out brought every hidden marker (and the open activity's own pin) back as clusters
        const shouldEnableClustering = currentZoom < 21 && !this.layer.featureEffect;

        if (shouldEnableClustering !== clusteringEnabled) {
          this.layer.featureReduction = shouldEnableClustering ? this.getClusteringConfig() : null;
          clusteringEnabled = shouldEnableClustering;
        }
      },
      { initial: false } // Don't run on initial - clustering already set at creation
    );
  }

  // ============================================================================
  // Private Methods - Click Handling
  // ============================================================================

  /**
   * Handle cluster multi-item click
   */
  private handleClusterMultiItemClick(
    clusterTypeUuid: string,
    event: any,
    context: MarkerClickContext
  ): void {
    if (!this.layer) return;

    this.layer.queryFeatures({
      where: `uuid = '${clusterTypeUuid}'`,
      outFields: ['*'],
      returnGeometry: true
    }).then(queryResult => {
      if (queryResult.features.length > 0) {
        context.onMultiItemClick(queryResult.features[0], event);
      }
    });
  }

  /**
   * Handle individual marker click
   */
  private handleMarkerClick(
    graphic: Graphic,
    event: any,
    context: MarkerClickContext,
    isDrilldownPicture: boolean = false
  ): void {
    // For drilldown pictures, go directly to routing - no cluster query needed
    if (isDrilldownPicture) {
      // Force markerType to 'picture' for drilldown pictures since it may not be in the layer attributes
      if (!graphic.attributes.markerType) {
        graphic.attributes.markerType = 'picture';
      }
      this.routeMarkerClick(graphic, event, context);
      return;
    }

    const clusterTypeUuid = graphic.attributes.cluster_type_uuid;

    if (clusterTypeUuid && this.layer) {
      // Query original feature
      this.layer.queryFeatures({
        where: `uuid = '${clusterTypeUuid}'`,
        outFields: ['*'],
        returnGeometry: true
      }).then(queryResult => {
        if (queryResult.features.length > 0) {
          this.routeMarkerClick(queryResult.features[0], event, context);
        }
      });
    } else {
      this.routeMarkerClick(graphic, event, context);
    }
  }

  /**
   * Route marker click to appropriate handler
   */
  private routeMarkerClick(
    graphic: Graphic,
    event: any,
    context: MarkerClickContext
  ): void {
    const markerType = graphic.attributes?.markerType;

    switch (markerType) {
      case 'picture':
        if (graphic.attributes?.is_same_location) {
          context.onSameLocationClick(graphic);
        } else {
          context.onPictureClick(graphic, event);
        }
        break;
      case 'bid_item':
        context.onBidItemClick(graphic, event);
        break;
      case 'activity':
        context.onActivityClick(graphic, event);
        break;
      case 'delivery':
        context.onDeliveryClick(graphic, event);
        break;
      case 'form':
        context.onFormClick(graphic, event);
        break;
      case 'multi_item_location':
        context.onMultiItemClick(graphic, event);
        break;
    }
  }

  /**
   * Cleanup event handlers (click and clustering only - pointer-move is persistent)
   */
  private cleanupHandlers(): void {
    // Clear hover state first
    this.clearHoverState();

    if (this.clickHandler) {
      this.clickHandler.remove();
      this.clickHandler = null;
    }
    // Note: pointerMoveHandler is NOT removed here - it's set up early and persists
    // It will be cleaned up only when cleanupAllHandlers() is called on destroy
    this.teardownClusteringZoomWatcher();
  }

  /**
   * Cleanup ALL event handlers including pointer-move (call on component destroy)
   */
  cleanupAllHandlers(): void {
    this.cleanupHandlers();
    if (this.pointerMoveHandler) {
      this.pointerMoveHandler.remove();
      this.pointerMoveHandler = null;
    }
  }
}
